import { MenuObjectTypes } from 'enums/menu';
import { IOffersState } from 'state/global-state/models/loyalty/offers/offers.types';
import { isOfferDiscount } from 'utils/cart/helper';
import { isCombo, isPicker } from 'utils/menu/is-menu-type';

import { IParsedLoyaltyReward } from './hooks/types';
import {
  LoyaltyAppliedOffer,
  LoyaltyOffer,
  SanityOffer,
  SanityOfferListItemWithExtras,
  WithIncentives,
  isLoyaltyOffer,
  isSanityOffer,
  isSanityOfferGroup,
} from './types';

export const isDiscountLoyaltyOffer = (offer: WithIncentives) =>
  'incentives' in offer &&
  offer.incentives?.every(benefit => benefit?._type === MenuObjectTypes.OFFER_DISCOUNT);

export const isDiscountOffer = (offer: WithIncentives) => isDiscountLoyaltyOffer(offer);

export const getCanStackOfferCheck = <T>(
  getOffer: (element: T) => LoyaltyOffer | LoyaltyAppliedOffer | null
) => (offer: LoyaltyOffer, list: T[]): boolean => {
  return (
    offer.isStackable ||
    !Array.isArray(list) ||
    list.length === 0 ||
    list.every((element: T) => {
      const offerItem = getOffer(element);
      // the stackable offer check is for offers only
      return offerItem ? offerItem?.isStackable : true;
    })
  );
};

type CmsOffersMap<T> = { [key: string]: T };

export function getCmsOffersMapByLoyaltyId(
  offerListItems: SanityOfferListItemWithExtras[] | null
): CmsOffersMap<SanityOffer> {
  return (offerListItems || []).reduce((acc, item) => {
    if (isSanityOffer(item) && item?.loyaltyEngineId) {
      acc[item.loyaltyEngineId] = item;
    }

    if (isSanityOfferGroup(item)) {
      item.offers?.forEach(offer => {
        if (isSanityOffer(offer) && offer?.loyaltyEngineId) {
          acc[offer.loyaltyEngineId] = offer;
        }
      });
    }

    return acc;
  }, {});
}

export const getCmsOffersMapByCmsId = (
  offers: IOffersState['cmsOffers'] | null
): { [key: string]: LoyaltyOffer } => {
  return (offers || []).reduce((acc, offer) => {
    if (isLoyaltyOffer(offer) && offer?._id) {
      acc[offer._id] = offer;
    }

    return acc;
  }, {});
};

export const incentiveIsOfferBenefit = (offer: Pick<LoyaltyOffer, 'incentives'>): boolean => {
  return !!offer.incentives?.every(incentive => {
    const isDiscount = isOfferDiscount(incentive);
    const isPickerOrComboWithBenefit =
      (isPicker(incentive) || isCombo(incentive)) && incentive.isOfferBenefit;

    return isDiscount || isPickerOrComboWithBenefit;
  });
};

/**
 * Util fn to find a reward in array and calculate if has reached limit per order
 */
export const findRewardInAppliedRewards = (
  appliedLoyaltyRewardsArray: IParsedLoyaltyReward[],
  engineRewardId: string,
  limitPerOrder?: number | null
) => {
  const appliedReward = appliedLoyaltyRewardsArray.find(
    appliedReward => appliedReward.rewardId === engineRewardId
  );

  if (!appliedReward || !limitPerOrder) {
    return { appliedReward, limitPerOrderMet: false };
  }

  return { appliedReward, limitPerOrderMet: appliedReward.timesApplied >= limitPerOrder };
};

/**
 * Upserts loyalty offer items by merging incoming items into the current list.
 *
 * Behavior:
 * - Incoming items with matching _id will replace existing items (useful for language updates or content refreshes)
 * - Incoming items with new _id values will be appended to the list
 * - Current items not present in incoming list are preserved
 * - Order of current items is maintained, with new items appended at the end
 *
 */
export const upsertSanityOfferLists = <T extends { _id: string }[]>(
  currentList: T,
  incomingList: T
): T => {
  if (currentList.length === 0) {
    return incomingList;
  }

  if (incomingList.length === 0) {
    return currentList;
  }

  // Create a map of incoming items by _id for O(1) lookup
  const incomingItemsMap = new Map(incomingList?.map(item => [item._id, item]) ?? []);

  // Replace existing items with incoming versions if they exist
  const updatedList = currentList.map(
    currentItem => incomingItemsMap.get(currentItem._id) ?? currentItem
  );

  // Add new items that don't exist in current list
  const currentIds = new Set(currentList.map(item => item._id));
  const newItems = incomingList?.filter(item => !currentIds.has(item._id)) ?? [];

  return [...updatedList, ...newItems] as T;
};
